import React, { forwardRef } from 'react';
import * as RadixTabsPrimitive from '@radix-ui/react-tabs';
import { Box } from '#components/box/box.tsx';

import { styled } from '../../theme';
import { GDSCustomizableComponent, GDSResponsiveProp } from '../../types';
import { useResponsiveProp } from '../../hooks/use-responsive-prop';
import { useComposedRefs } from '../../hooks/use-composed-ref';
import { useTabsScroll } from './use-tabs-scroll';
import {
  ScrollableWrapper,
  ScrollableItemsWrapper,
  ScrollBack,
  ScrollForward,
} from './scroll-buttons';
import { TabListContextProvider } from './tablist-context';
import { GenesisCoreInspector } from '../../test-utils/genesis-core-inspector';

export interface GDSTabListProps
  extends RadixTabsPrimitive.TabsListProps,
    GDSCustomizableComponent {
  divider?: boolean;
  tabDistribution?: GDSResponsiveProp<'distributeEvenly' | 'hugContents'>;
}

const DISPLAY_NAME = 'TabList';

const BaseTablist = styled(RadixTabsPrimitive.List, {
  display: 'flex',
  gap: '$half',
  overflowX: 'auto',
  scrollBehavior: 'smooth',
  scrollbarWidth: 'none' /* Firefox */,
  '&::-webkit-scrollbar': {
    display: 'none',
    width: '0px',
    background: 'transparent' /* make scrollbar transparent */,
  },
  variants: {
    tabDistribution: {
      distributeEvenly: {
        justifyContent: 'stretch',
        '> .GDS-tab': {
          flex: '1 0 auto',
        },
      },
      hugContents: {
        justifyContent: 'flex-start',
      },
    },
  },
});

export const TabList = forwardRef<HTMLDivElement, GDSTabListProps>(
  (
    {
      children,
      className,
      css,
      divider,
      loop = false,
      tabDistribution = 'distributeEvenly',
      'data-testid': testId = `GDS-${DISPLAY_NAME}`,
      ...props
    },
    ref,
  ) => {
    const responsiveTabDistribution = useResponsiveProp(tabDistribution);
    const responsiveDivider = useResponsiveProp(divider);

    const {
      scrollBack,
      scrollForward,
      tabsList,
      showScrollBack,
      showScrollForward,
    } = useTabsScroll();
    const composedRefs = useComposedRefs(
      ref,
      tabsList as React.Ref<HTMLDivElement>,
    );
    const tabListWrapperRef = React.useRef(null);
    const scrollBackButtonRef = React.useRef(null);
    const scrollForwardButtonRef = React.useRef(null);

    const tabListElements = {
      tabListWrapper: tabListWrapperRef.current,
      scrollBackButton: scrollBackButtonRef.current,
      scrollForwardButton: scrollForwardButtonRef.current,
    };

    return (
      <GenesisCoreInspector displayName={DISPLAY_NAME}>
        <TabListContextProvider tabListElements={tabListElements}>
          <Box css={css}>
            <ScrollableWrapper>
              {showScrollBack && (
                <ScrollBack
                  ref={scrollBackButtonRef}
                  onClick={() => scrollBack()}
                />
              )}
              <ScrollableItemsWrapper
                className="GDS-tab-list-wrapper"
                ref={tabListWrapperRef}
                css={{
                  ...(responsiveDivider && {
                    borderBlockEnd:
                      '$borderWidths$thin solid $onSurfaceBorderSubdued',
                  }),
                }}
              >
                <BaseTablist
                  ref={composedRefs}
                  className={[`GDS-tab-list`, className].join(' ')}
                  tabDistribution={responsiveTabDistribution}
                  loop={loop}
                  data-testid={testId}
                  {...props}
                >
                  {children}
                </BaseTablist>
              </ScrollableItemsWrapper>
              {showScrollForward && (
                <ScrollForward
                  ref={scrollForwardButtonRef}
                  onClick={() => scrollForward()}
                />
              )}
            </ScrollableWrapper>
          </Box>
        </TabListContextProvider>
      </GenesisCoreInspector>
    );
  },
);
